/** Form primitives: Input, Textarea, Label, Select (native), Checkbox. */
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactElement,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cn } from "@/shared/lib/format";

const fieldClass =
  "flex min-w-0 max-w-full w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-base sm:text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

/** Text input. */
export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return <input className={cn(fieldClass, "h-8", className)} {...props} />;
}

/** Multi-line input. */
export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactElement {
  return <textarea className={cn(fieldClass, "min-h-16", className)} {...props} />;
}

/** Native select styled like the other fields (keyboard and RTL friendly). */
export function Select({
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>): ReactElement {
  return <select className={cn(fieldClass, "h-auto min-h-9", className)} {...props} />;
}

/** Form label. */
export function Label({
  className,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement>): ReactElement {
  return (
    <label
      className={cn("text-xs font-semibold leading-none text-foreground", className)}
      {...props}
    />
  );
}

/** Checkbox with inline label (forwards `ref` so callers can set `indeterminate`). */
export function Checkbox({
  label,
  className,
  id,
  ref,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  ref?: Ref<HTMLInputElement>;
}): ReactElement {
  return (
    <label
      htmlFor={id}
      className={cn(
        "inline-flex min-w-0 items-center gap-2 text-xs [overflow-wrap:anywhere]",
        className,
      )}
    >
      <input
        id={id}
        ref={ref}
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 accent-primary"
        {...props}
      />
      {label}
    </label>
  );
}

/** A labelled field wrapper with optional error message. */
export function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: ReactElement;
}): ReactElement {
  return (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && !error ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
      {error ? (
        <p className="text-[11px] text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
